import React, { ReactElement, ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import { HeadingComponent } from 'react-markdown/src/ast-to-react'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import kebabcase from 'lodash.kebabcase'

import { BlockText, Text } from '../../../library/elements/Text'
import { IProps } from './index'
import styles from './styles.scss'
import useTranslate from '../../hooks/useTranslate'

export const LegalSection = ({ heading, effectiveDate, content }: IProps): React.ReactElement => {
  const { translate } = useTranslate()
  function flatten(previousText: string, child: ReactNode): string {
    if (typeof child === 'string' || typeof child === 'number') {
      return previousText + child.toString()
    } else if ((child as ReactElement).props) {
      return previousText + (child as ReactElement).props.children
    }
    return previousText
  }

  const HeadingRenderer: HeadingComponent = ({ level, children }) => {
    const text = children.reduce<string>(flatten, '')
    const slug = text.replace(/^\d+\\?\.\ /g, '')
    return React.createElement('h' + level, { id: kebabcase(slug) }, children)
  }

  return (
    <div className="legal-section">
      <div className="legal-section__header">
        <h2 className="legal-section__title">
          <Text brand="default" size="heading-3">
            {heading}
          </Text>
        </h2>

        <button className="legal-section__print" onClick={() => window.print()}>
          <svg
            className="legal-section__print-icon"
            xmlns="http://www.w3.org/2000/svg"
            height="20"
            viewBox="0 0 24 24"
            width="24"
          >
            <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z" />
            <path d="M0 0h24v24H0z" fill="none" />
          </svg>
          {translate('Print page')}
        </button>
      </div>

      <div className="legal-section__info">
        {effectiveDate && (
          <span className="legal-section__effective-date">
            <Text brand="default" size="eyebrows">
              {translate('Effective day')} {effectiveDate}
            </Text>
          </span>
        )}
      </div>

      <div className="legal-section__content">
        <BlockText brand="default" size="body">
          {content.map((markdown) => (
            <>
              {markdown.heading ? (
                <h3 className="legal-section__content-title">{markdown.heading}</h3>
              ) : null}
              <ReactMarkdown
                key={markdown.heading}
                components={{
                  h1: HeadingRenderer,
                  h2: HeadingRenderer,
                  h3: HeadingRenderer,
                  h4: HeadingRenderer,
                  h5: HeadingRenderer,
                  h6: HeadingRenderer,
                }}
                // Since Contentful uses the GitHub markdown flavor,
                // it could be useful to have this flavor integrated through Remark plugins.
                // https://www.contentful.com/blog/2020/05/25/beginners-guide-to-contentful-text-types-markdown-richtext/
                // https://www.npmjs.com/package/remark-gfm
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
              >
                {markdown.content}
              </ReactMarkdown>
            </>
          ))}
        </BlockText>
      </div>
      <style jsx>{styles}</style>
    </div>
  )
}
